import { useTranslation } from 'react-i18next';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';

import { FilterIndicator } from './Layout';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';

type Event = GetEventRegistrationsStats['event'];

interface TotalsProps {
  event: Event;
}

const Totals = ({ event }: TotalsProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatInteger } = useLocale();
  const device = useMediaDevice();

  return (
    <UI.Card sc={{ textAlign: 'center', padding: 0 }} data-testid="DashboardTotals">
      <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr 1fr 1fr 1fr'], gutter: 0 }}>
        <UI.Div
          sc={{ padding: [2, 3] }}
          style={{
            border: 'solid rgba(0, 0, 0, .08)',
            borderWidth: device.isSmall ? '0 0 1px 0' : '0 1px 0 0',
          }}
        >
          <UI.H3 sc={{ mb: 1, fontSize: 4 }} style={{ fontWeight: 300 }}>
            {formatInteger(event.registrations_count)}
          </UI.H3>
          <UI.Label
            sc={{
              background: event.new_registrations_count > 0 ? 'primary.25' : undefined,
              color: event.new_registrations_count > 0 ? 'primary.700' : 'gray.500',
            }}
            style={{ fontWeight: 500 }}
          >
            <UI.Small>
              {'+ '}
              {formatInteger(event.new_registrations_count)}
              {' '}
              {t('today_lower')}
            </UI.Small>
          </UI.Label>
          <UI.FlexContainer sc={{ justifyContent: 'center', alignItems: 'center', mt: 2 }}>
            <UI.Div sc={{ fontWeight: 600 }}>
              {t('registrations', { count: event.registrations_count })}
            </UI.Div>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
        </UI.Div>
        <UI.Div
          sc={{ padding: [2, 3] }}
          style={{
            border: 'solid rgba(0, 0, 0, .08)',
            borderWidth: device.isSmall ? '0 0 1px 0' : '0 1px 0 0',
          }}
        >
          <UI.H3 sc={{ mb: 1, fontSize: 4 }} style={{ fontWeight: 300 }}>
            {formatInteger(event.teams_count)}
          </UI.H3>
          <UI.Label
            sc={{
              background: event.new_teams_count > 0 ? 'primary.25' : undefined,
              color: event.new_teams_count > 0 ? 'primary.700' : 'gray.500',
            }}
            style={{ fontWeight: 500 }}
          >
            <UI.Small>
              {'+ '}
              {formatInteger(event.new_teams_count)}
              {' '}
              {t('today_lower')}
            </UI.Small>
          </UI.Label>
          <UI.FlexContainer sc={{ justifyContent: 'center', alignItems: 'center', mt: 2 }}>
            <UI.Div sc={{ fontWeight: 600 }}>
              {t('teams', { count: event.teams_count })}
            </UI.Div>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
        </UI.Div>
        <UI.Div
          sc={{ padding: [2, 3] }}
          style={{
            border: 'solid rgba(0, 0, 0, .08)',
            borderWidth: device.isSmall ? '0 0 1px 0' : '0 1px 0 0',
          }}
        >
          <UI.H3 sc={{ mb: 1, fontSize: 4 }} style={{ fontWeight: 300 }}>
            {formatInteger(event.orders_count)}
          </UI.H3>
          <UI.Label
            sc={{
              background: event.new_orders_count > 0 ? 'primary.25' : undefined,
              color: event.new_orders_count > 0 ? 'primary.700' : 'gray.500',
            }}
            style={{ fontWeight: 500 }}
          >
            <UI.Small>
              {'+ '}
              {formatInteger(event.new_orders_count)}
              {' '}
              {t('today_lower')}
            </UI.Small>
          </UI.Label>
          <UI.FlexContainer sc={{ justifyContent: 'center', alignItems: 'center', mt: 2 }}>
            <UI.Div sc={{ fontWeight: 600 }}>
              {t('orders', { count: event.orders_count })}
            </UI.Div>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
        </UI.Div>
        <UI.Div
          sc={{ padding: [2, 3] }}
          style={{
            border: 'solid rgba(0, 0, 0, .08)',
            borderWidth: device.isSmall ? '0 0 1px 0' : '0 1px 0 0',
          }}
        >
          <UI.H3 sc={{ mb: 1, fontSize: 4 }} style={{ fontWeight: 300 }}>
            {formatCurrency(event.revenue, {
              minimumFractionDigits: 0, maximumFractionDigits: 0,
            })}
          </UI.H3>
          <UI.Label
            sc={{
              background: event.new_revenue > 0 ? 'primary.25' : undefined,
              color: event.new_revenue > 0 ? 'primary.700' : 'gray.500',
            }}
            style={{ fontWeight: 500 }}
          >
            <UI.Small>
              {'+ '}
              {formatCurrency(Math.max(event.new_revenue, 0), {
                minimumFractionDigits: 0, maximumFractionDigits: 0,
              })}
              {' '}
              {t('today_lower')}
            </UI.Small>
          </UI.Label>
          <UI.FlexContainer sc={{ justifyContent: 'center', alignItems: 'center', mt: 2 }}>
            <UI.Div sc={{ fontWeight: 600 }}>
              {t('revenue')}
            </UI.Div>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
        </UI.Div>
        <UI.Div sc={{ padding: [2, 3] }} style={{ minWidth: 0 }}>
          <UI.H3 sc={{ mb: 1, fontSize: 4 }} style={{ fontWeight: 300 }}>
            {formatInteger(event.sessions_count)}
          </UI.H3>
          <UI.Label
            sc={{
              background: event.queue_size > 0 ? 'info.100' : undefined,
              color: event.queue_size > 0 ? 'info.700' : 'gray.500',
            }}
            style={{ fontWeight: 500 }}
          >
            <UI.Small>
              {'+ '}
              {t('n_in_queue', { size: formatInteger(event.queue_size) })}
            </UI.Small>
          </UI.Label>
          <UI.Div sc={{ fontWeight: 600, textAlign: 'center', mt: 2 }}>
            {t('active_registration_sessions', { count: event.sessions_count })}
          </UI.Div>
        </UI.Div>
      </UI.GridContainer>
    </UI.Card>
  );
};

export default Totals;
